/**
 * KAN-743: каталог товаров интернет-магазина (только чтение).
 *
 *   GET /api/v1/catalog/categories/
 *   GET /api/v1/catalog/products/?category=&include_children=1&search=&available=&page=&page_size=
 *   GET /api/v1/catalog/products/<id>/
 *
 * Организация не в режиме интернет-магазина получает 404.
 *
 * KAN-754: ручные категории и товары (только ручные, у фидовых 400/403):
 *   POST   /api/v1/catalog/categories/          {name, parent_id?}
 *   PATCH  /api/v1/catalog/categories/<id>/
 *   DELETE /api/v1/catalog/categories/<id>/
 *   POST   /api/v1/catalog/products/            CatalogProductBody
 *   PATCH  /api/v1/catalog/products/<id>/
 *   DELETE /api/v1/catalog/products/<id>/
 */
import apiClient from './client';
import type {
  CatalogCategoriesResponse,
  CatalogCategory,
  CatalogCategoryBody,
  CatalogId,
  CatalogProductBody,
  CatalogProductDetail,
  CatalogProductsParams,
  CatalogProductsResponse,
} from '@/types/catalog';

const BASE = '/api/v1/catalog/';

const itemUrl = (kind: 'categories' | 'products', id: CatalogId): string =>
  `${BASE}${kind}/${encodeURIComponent(String(id))}/`;

/** Пустые фильтры в запрос не уходят. */
function cleanParams(params: CatalogProductsParams): Record<string, string | number> {
  const out: Record<string, string | number> = {};
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') out[key] = value;
  });
  return out;
}

export const catalogApi = {
  categories: async (): Promise<CatalogCategoriesResponse> =>
    (await apiClient.get(`${BASE}categories/`)).data,
  products: async (params: CatalogProductsParams): Promise<CatalogProductsResponse> =>
    (await apiClient.get(`${BASE}products/`, { params: cleanParams(params) })).data,
  product: async (id: string): Promise<CatalogProductDetail> =>
    (await apiClient.get(itemUrl('products', id))).data,

  createCategory: async (body: CatalogCategoryBody): Promise<CatalogCategory> =>
    (await apiClient.post(`${BASE}categories/`, body)).data,
  updateCategory: async (id: CatalogId, body: Partial<CatalogCategoryBody>): Promise<CatalogCategory> =>
    (await apiClient.patch(itemUrl('categories', id), body)).data,
  deleteCategory: async (id: CatalogId): Promise<void> => {
    await apiClient.delete(itemUrl('categories', id));
  },

  createProduct: async (body: CatalogProductBody): Promise<CatalogProductDetail> =>
    (await apiClient.post(`${BASE}products/`, body)).data,
  updateProduct: async (id: CatalogId, body: Partial<CatalogProductBody>): Promise<CatalogProductDetail> =>
    (await apiClient.patch(itemUrl('products', id), body)).data,
  deleteProduct: async (id: CatalogId): Promise<void> => {
    await apiClient.delete(itemUrl('products', id));
  },
};
